/**
 * Front script: viewport-width hide and show-after-anchor reveal.
 */

const BAR_SELECTOR = '.wp-block-kodefix-sticky-bar.kfx-sticky-bar';
const HIDDEN_CLASS = 'kfx-sticky-bar--hidden';
const REVEALED_CLASS = 'kfx-sticky-bar--anchor-revealed';
const FADING_OUT_CLASS = 'kfx-sticky-bar--fading-out';

type AnchorRevealMode = 'enters' | 'leaves';

function parsePositiveInt(value: string | undefined, fallback = 0): number {
  if (value === undefined || value === '') {
    return fallback;
  }
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function getBars(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(BAR_SELECTOR));
}

function syncVisibility(bar: HTMLElement): void {
  const hideBelow = parsePositiveInt(bar.dataset.hideBelow, 0);
  if (hideBelow <= 0) {
    bar.classList.remove(HIDDEN_CLASS);
    return;
  }
  const shouldHide = window.innerWidth < hideBelow;
  bar.classList.toggle(HIDDEN_CLASS, shouldHide);
}

function refresh(): void {
  for (const bar of getBars()) {
    syncVisibility(bar);
  }
}

function getRevealMode(bar: HTMLElement): AnchorRevealMode {
  return bar.dataset.anchorReveal === 'leaves' ? 'leaves' : 'enters';
}

/**
 * enters — reveal once the anchor reaches the viewport (in view or already above).
 * leaves — reveal once the anchor has scrolled fully above the viewport.
 */
function shouldReveal(
  mode: AnchorRevealMode,
  rect: DOMRectReadOnly,
  isIntersecting: boolean
): boolean {
  if (mode === 'leaves') {
    return rect.bottom < 0;
  }
  // enters: visible on screen, or already scrolled past (must have entered).
  return isIntersecting || rect.top < 0;
}

function revealBar(bar: HTMLElement): void {
  bar.classList.remove(FADING_OUT_CLASS);
  bar.classList.add(REVEALED_CLASS);
}

function hideBar(bar: HTMLElement): void {
  if (!bar.classList.contains(REVEALED_CLASS)) {
    return;
  }
  if (bar.classList.contains(FADING_OUT_CLASS)) {
    return;
  }

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  if (reduceMotion) {
    bar.classList.remove(REVEALED_CLASS, FADING_OUT_CLASS);
    return;
  }

  bar.classList.add(FADING_OUT_CLASS);

  const onEnd = (event: TransitionEvent): void => {
    if (event.propertyName !== 'opacity' || event.target !== bar) {
      return;
    }
    bar.removeEventListener('transitionend', onEnd);
    if (!bar.classList.contains(FADING_OUT_CLASS)) {
      return;
    }
    bar.classList.remove(REVEALED_CLASS, FADING_OUT_CLASS);
  };

  bar.addEventListener('transitionend', onEnd);
}

function applyRevealState(
  bar: HTMLElement,
  mode: AnchorRevealMode,
  rect: DOMRectReadOnly,
  isIntersecting: boolean
): void {
  if (shouldReveal(mode, rect, isIntersecting)) {
    revealBar(bar);
  } else {
    hideBar(bar);
  }
}

function bindShowAfterAnchor(bar: HTMLElement): void {
  const anchorId = bar.dataset.showAfterAnchor?.trim();
  if (!anchorId) {
    return;
  }

  const target = document.getElementById(anchorId);
  if (!target) {
    // Missing target must not leave the bar permanently hidden.
    revealBar(bar);
    return;
  }

  const mode = getRevealMode(bar);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        applyRevealState(
          bar,
          mode,
          entry.boundingClientRect,
          entry.isIntersecting
        );
      }
    },
    { threshold: 0 }
  );

  observer.observe(target);

  // Fallback: large scroll jumps can skip IntersectionObserver updates.
  let ticking = false;
  const onScrollOrResize = (): void => {
    if (ticking) {
      return;
    }
    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;
      const rect = target.getBoundingClientRect();
      const isIntersecting =
        rect.top < window.innerHeight && rect.bottom > 0;
      applyRevealState(bar, mode, rect, isIntersecting);
    });
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  onScrollOrResize();
}

function init(): void {
  const bars = getBars();
  if (bars.length === 0) {
    return;
  }

  refresh();
  window.addEventListener('resize', refresh);

  for (const bar of bars) {
    bindShowAfterAnchor(bar);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
